import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Calendar, Users, UtensilsCrossed, Clock, Plus, CheckCircle, XCircle, UserCheck, ChevronDown, ChevronUp, Moon, Sun } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
// Dev components removed - using Supabase instead
import { localDatabase } from '../services/localDatabase'
import { subscribeCrossTab } from '../services/crossTab'
import { useTenant } from '../contexts/TenantContext'

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { tenantId, dbReady } = useTenant()
  
  // Activity types and their configurations
  const activityConfig = {
    newReservation: { icon: Plus, color: 'text-blue-500', bgColor: 'bg-blue-50' },
    customerArrived: { icon: UserCheck, color: 'text-green-500', bgColor: 'bg-green-50' },
    tableFreed: { icon: CheckCircle, color: 'text-purple-500', bgColor: 'bg-purple-50' },
    reservationCancelled: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-50' },
    customerAdded: { icon: Users, color: 'text-indigo-500', bgColor: 'bg-indigo-50' }
  }

  // Real-time stats state
  const [todayStats, setTodayStats] = useState({
    totalReservations: 0,
    totalGuests: 0,
    availableTables: 0,
    totalTables: 0,
    cancellationsToday: 0
  })

  // State for recent activities
  const [recentActivities, setRecentActivities] = useState<Array<{
    id: string
    type: keyof typeof activityConfig
    message: string
    timestamp: Date
    details?: string
  }>>([])

  // State for activity container
  const [showAllActivities, setShowAllActivities] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // Theme state mirrors current DOM on mount and only updates DOM when user toggles
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    } catch {
      return false
    }
  })
  const userInitiatedThemeChangeRef = useRef(false)

  // Quick action handlers
  const handleNewReservation = () => {
    navigate('/reservations', { state: { openNewReservation: true } })
  }

  const handleViewTables = () => {
    navigate('/tables')
  }

  const handleManageCustomers = () => {
    navigate('/customers')
  }

  // Function to add new activity (will be called from other components)
  const addActivity = (type: keyof typeof activityConfig, message: string, details?: string) => {
    const newActivity = {
      id: Date.now().toString(),
      type,
      message,
      timestamp: new Date(),
      details
    }
    
    setRecentActivities(prev => [newActivity, ...prev.slice(0, 19)]) // Keep 20 most recent for scrolling
  }

  // Function to fetch real-time stats
  const fetchRealTimeStats = useCallback(async () => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    try {
      // Get today's date
      const today = new Date()
      const todayString = today.toISOString().split('T')[0]
      
      // Fetch reservations for today
      const todayReservations = await localDatabase.getReservationsByDate(todayString)
      const cancellationsToday = todayReservations.filter(
        (res) => (res.status || '').toLowerCase() === 'cancelled'
      ).length
      const activeReservations = todayReservations.filter(res => res.status !== 'cancelled' && res.status !== 'no-show')
      const totalReservations = activeReservations.length
      const totalGuests = activeReservations.reduce((sum, res) => sum + (res.party_size || 0), 0)

      // Fetch tables and align availability logic with Tables view
      const allTables = await localDatabase.getAllTables()
      const RESERVATION_DURATION_MILLIS = 2 * 60 * 60 * 1000 // 2 hours
      const PRE_RESERVATION_BUFFER_MILLIS = 2 * 60 * 60 * 1000 // 2 hours
      const now = new Date()

      const normalizedTables = allTables.map(table => {
        const status = (table.status || '').toLowerCase()
        const isActiveTable =
          typeof (table as any).is_active === 'boolean'
            ? (table as any).is_active
            : !['inactive', 'maintenance'].includes(status)

        const tableReservations = todayReservations.filter(
          res => res.table_id === table.id && res.status !== 'cancelled'
        )

        const currentReservation = tableReservations.find(res => {
          const reservationTime = new Date(`${res.date}T${res.time}:00`)
          const endTime = new Date(reservationTime.getTime() + RESERVATION_DURATION_MILLIS)
          return now >= reservationTime && now <= endTime
        })

        const nextReservation = tableReservations
          .filter(res => {
            const reservationTime = new Date(`${res.date}T${res.time}:00`)
            return reservationTime > now
          })
          .sort(
            (a, b) =>
              new Date(`${a.date}T${a.time}:00`).getTime() -
              new Date(`${b.date}T${b.time}:00`).getTime()
          )[0]

        let displayStatus: 'available' | 'occupied' | 'reserved' = 'available'

        if (['occupied', 'reserved'].includes(status)) {
          displayStatus = status as 'occupied' | 'reserved'
        } else if (!isActiveTable) {
          displayStatus = 'reserved'
        } else if (currentReservation) {
          displayStatus = 'occupied'
        } else if (nextReservation) {
          const reservationTime = new Date(`${nextReservation.date}T${nextReservation.time}:00`)
          const timeUntilReservation = reservationTime.getTime() - now.getTime()
          displayStatus =
            timeUntilReservation <= PRE_RESERVATION_BUFFER_MILLIS ? 'reserved' : 'available'
        }

        return { displayStatus, isActiveTable }
      })

      const availableTables = normalizedTables.filter(t => t.displayStatus === 'available').length
      const totalTables = allTables.length

      setTodayStats({
        totalReservations,
        totalGuests,
        availableTables,
        totalTables,
        cancellationsToday
      })
    } catch (error) {
      if (error instanceof Error && error.message === 'Database not initialized') {
        return // Transient race before DB is ready; skip logging/Sentry
      }
      console.error('Error fetching real-time stats:', error)
    }
  }, [tenantId, dbReady])

  // Apply dark mode to document and persist preference ONLY after explicit user toggle
  useEffect(() => {
    if (!userInitiatedThemeChangeRef.current) return
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    try {
      localStorage.setItem('darkMode', JSON.stringify(isDarkMode))
    } catch {}
  }, [isDarkMode])

  // Function to generate activities from real database data (production-ready)
  const generateActivitiesFromData = useCallback(async () => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    try {
      const activities: Array<{
        id: string
        type: keyof typeof activityConfig
        message: string
        timestamp: Date
        details?: string
      }> = []

      // Get all reservations and filter for recent ones (last 7 days)
      const allReservations = await localDatabase.getAllReservations()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
      
      // Filter reservations from the last 7 days and sort by creation time (most recent first)
      const recentReservations = allReservations
        .filter(reservation => {
          const createdDate = new Date(reservation.created_at || reservation.updated_at || 0)
          return createdDate >= sevenDaysAgo
        })
        .sort((a, b) => 
          new Date(b.created_at || b.updated_at || 0).getTime() - 
          new Date(a.created_at || a.updated_at || 0).getTime()
        )

      // Convert reservations to activities (limit to 20 most recent)
      for (const reservation of recentReservations.slice(0, 20)) {
        let tableDisplay = 'N/A'
        if (reservation.table_id) {
          try {
            const table = await localDatabase.getTable(reservation.table_id)
            if (table) {
              tableDisplay = table.number.toString()
            }
          } catch (error) {
            console.warn('Failed to fetch table for activity display:', error)
            tableDisplay = 'N/A'
          }
        }

        // Resolve customer surname/name
        let customerDisplay = 'Άγνωστος πελάτης'
        try {
          if (reservation.customer_id) {
            const customer = await localDatabase.getCustomer(reservation.customer_id)
            if (customer?.name) {
              const parts = customer.name.trim().split(/\s+/)
              if (parts.length === 1) {
                customerDisplay = parts[0]
              } else {
                const lastName = parts[parts.length - 1]
                const firstName = parts[0]
                const initial = firstName.charAt(0).toUpperCase()
                customerDisplay = `${lastName} ${initial}.`
              }
            }
          }
        } catch (error) {
          console.warn('Failed to fetch customer for activity display:', error)
        }

        const createdDate = new Date(reservation.created_at || reservation.updated_at || Date.now())
        
        activities.push({
          id: `reservation-${reservation.id}`,
          type: 'newReservation' as const,
          message: customerDisplay,
          timestamp: createdDate,
          details: `Τραπέζι ${tableDisplay} • ${reservation.party_size || 0} άτομα • ${reservation.time || 'N/A'}`
        })
      }

      // Production-ready: No mock data fallback
      // If no activities exist, the UI will show the empty state

      setRecentActivities(activities)
      setIsLoading(false)
    } catch (error) {
      if (error instanceof Error && error.message === 'Database not initialized') {
        setIsLoading(false)
        return // Transient race before DB is ready; skip logging/Sentry
      }
      console.error('Error generating activities:', error)
      setIsLoading(false)
    }
  }, [tenantId, dbReady])

  // Function to format time ago
  const formatTimeAgo = (timestamp: Date): string => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Τώρα'
    if (diffInMinutes < 60) return `Πριν από ${diffInMinutes} λεπτά`
    if (diffInMinutes < 1440) return `Πριν από ${Math.floor(diffInMinutes / 60)} ώρες`
    return `Πριν από ${Math.floor(diffInMinutes / 1440)} ημέρες`
  }

  // Load initial data and set up real-time updates
  useEffect(() => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    
    const initializeData = async () => {
      await Promise.all([
        fetchRealTimeStats(),
        generateActivitiesFromData()
      ])
    }
    
    initializeData()
    
    // Set up real-time updates every 30 seconds
    const statsInterval = setInterval(fetchRealTimeStats, 30000)
    const activitiesInterval = setInterval(generateActivitiesFromData, 30000)
    
    // Subscribe to cross-tab messages to refresh when reservations are created/updated
    const unsubscribe = subscribeCrossTab((message) => {
      if (message.type === 'sync:reservations') {
        generateActivitiesFromData()
        fetchRealTimeStats()
      }
    })
    
    return () => {
      clearInterval(statsInterval)
      clearInterval(activitiesInterval)
      unsubscribe()
    }
  }, [tenantId, dbReady, fetchRealTimeStats, generateActivitiesFromData])

  // Expose addActivity function globally for other components to use
  useEffect(() => {
    // @ts-ignore - Exposing function globally for other components
    window.addDashboardActivity = addActivity
  }, [])



  const StatCard: React.FC<{
    title: string
    value: number
    icon: React.ElementType
    description: string
    color: string
  }> = ({ title, value, icon: Icon, description, color }) => (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6 card-hover min-w-0 overflow-hidden">
      <div className="flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <p className="text-xs md:text-sm font-medium text-muted-foreground flex-1 pr-2 leading-tight">{title}</p>
          <div className={`p-1.5 md:p-2 rounded-lg ${color} flex-shrink-0 ml-2`}>
            <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div className="flex-1">
            <p className="text-2xl md:text-3xl font-bold text-card-foreground">{value}</p>
            <p className="text-xs md:text-sm text-muted-foreground mt-1 hidden sm:block leading-tight">{description}</p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-4 md:space-y-6 w-full min-w-0 overflow-x-hidden">
      {/* Page Header */}
      <div>
        <h1 className="text-xl md:text-3xl font-bold text-foreground">Αρχική Σελίδα</h1>
        <p className="text-sm md:text-base text-muted-foreground mt-1 md:mt-2 hidden sm:block">Επισκόπηση της σημερινής δραστηριότητας του εστιατορίου</p>
      </div>

      {/* Stats Grid — 2×2 on small screens, 4 columns from lg */}
      <div className="grid grid-cols-2 grid-rows-2 md:grid-cols-2 md:grid-rows-2 lg:grid-cols-4 lg:grid-rows-1 gap-3 md:gap-6 w-full min-w-0">
        <StatCard
          title="Κρατήσεις Σήμερα"
          value={todayStats.totalReservations}
          icon={Calendar}
          description="Συνολικές κρατήσεις"
          color="bg-blue-500"
        />
        <StatCard
          title="Διαθέσιμα Τραπέζια"
          value={todayStats.availableTables}
          icon={UtensilsCrossed}
          description={`Από σύνολο ${todayStats.totalTables}`}
          color="bg-green-500"
        />
        <StatCard
          title="Σύνολο Επισκεπτών"
          value={todayStats.totalGuests}
          icon={Users}
          description="Άτομα που αναμένονται"
          color="bg-purple-500"
        />
        <StatCard
          title="Ακυρώσεις"
          value={todayStats.cancellationsToday}
          icon={XCircle}
          description="Κρατήσεις με κατάσταση ακύρωσης σήμερα"
          color="bg-red-500"
        />
      </div>

      {/* Today's Schedule Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Quick Actions */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold text-card-foreground mb-3 md:mb-4">Γρήγορες Ενέργειες</h2>
          <div className="space-y-2 md:space-y-3">
            <button 
              onClick={handleNewReservation}
              className="w-full text-left p-4 md:p-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md hover:shadow-lg transition-all duration-200 touch-target-large min-h-[70px] flex items-center mobile-touch-feedback focus:outline-none focus:ring-2 focus:ring-blue-400 active:scale-[0.98] border-t border-white/10 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="flex-1">
                <div className="text-sm md:text-base font-semibold tracking-tight">Νέα Κράτηση</div>
                <div className="text-xs md:text-sm text-blue-50/90 hidden sm:block font-medium">Προσθήκη νέας κράτησης</div>
              </div>
              <Plus className="w-6 h-6 text-white/90" />
            </button>
            <button 
              onClick={handleViewTables}
              className="w-full text-left p-4 md:p-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md hover:shadow-lg transition-all duration-200 touch-target-large min-h-[70px] flex items-center mobile-touch-feedback focus:outline-none focus:ring-2 focus:ring-emerald-400 active:scale-[0.98] border-t border-white/10 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="flex-1">
                <div className="text-sm md:text-base font-semibold tracking-tight">Προβολή Τραπεζιών</div>
                <div className="text-xs md:text-sm text-emerald-50/90 hidden sm:block font-medium">Έλεγχος κατάστασης</div>
              </div>
              <UtensilsCrossed className="w-6 h-6 text-white/90" />
            </button>
            <button 
              onClick={handleManageCustomers}
              className="w-full text-left p-4 md:p-3 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-md hover:shadow-lg transition-all duration-200 touch-target-large min-h-[70px] flex items-center mobile-touch-feedback focus:outline-none focus:ring-2 focus:ring-purple-400 active:scale-[0.98] border-t border-white/10 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="flex-1">
                <div className="text-sm md:text-base font-semibold tracking-tight">Διαχείριση Πελατών</div>
                <div className="text-xs md:text-sm text-purple-50/90 hidden sm:block font-medium">Προβολή και επεξεργασία</div>
              </div>
              <Users className="w-6 h-6 text-white/90" />
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="text-lg md:text-xl font-semibold text-card-foreground">Πρόσφατη Δραστηριότητα</h2>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setRecentActivities([])}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                title="Καθαρισμός δραστηριότητας"
              >
                Καθαρισμός
              </button>
              <button 
                onClick={() => setShowAllActivities(!showAllActivities)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                title={showAllActivities ? "Εμφάνιση λιγότερων" : "Εμφάνιση περισσότερων"}
              >
                {showAllActivities ? (
                  <>
                    <span>Λιγότερα</span>
                    <ChevronUp className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    <span>Περισσότερα</span>
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className={`space-y-2 md:space-y-3 ${showAllActivities ? 'max-h-96' : 'max-h-80'} overflow-y-auto pr-2 activity-scrollbar`}>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-sm">Φόρτωση δραστηριότητας...</p>
              </div>
            ) : recentActivities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">Δεν υπάρχει πρόσφατη δραστηριότητα</p>
              </div>
            ) : (
              recentActivities.slice(0, showAllActivities ? 20 : 5).map((activity) => {
                const config = activityConfig[activity.type]
                const Icon = config.icon
                
                return (
                  <div 
                    key={activity.id}
                    className={`flex items-center space-x-3 p-2 md:p-3 ${config.bgColor} rounded-lg transition-all duration-200 hover:shadow-sm`}
                  >
                    <Icon className={`w-4 h-4 md:w-5 md:h-5 ${config.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs md:text-sm font-medium text-gray-900 truncate">
                        {activity.message}
                      </div>
                      {activity.details && (
                        <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                          {activity.details}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTimeAgo(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
            
            {!showAllActivities && recentActivities.length > 5 && (
              <div className="text-center py-2 text-gray-400">
                <p className="text-xs">+{recentActivities.length - 5} περισσότερες δραστηριότητες</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Development Tools removed - using Supabase instead */}

      {/* Mobile-only Settings Section */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:hidden">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-card-foreground">Ρυθμίσεις</h2>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isDarkMode ? (
              <Moon className="w-5 h-5 text-yellow-500" />
            ) : (
              <Sun className="w-5 h-5 text-orange-500" />
            )}
            <span className="text-sm text-muted-foreground">Σκοτεινή λειτουργία</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isDarkMode}
            onClick={() => {
              userInitiatedThemeChangeRef.current = true
              setIsDarkMode(v => !v)
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'bg-primary' : 'bg-muted'}`}
            aria-label="Εναλλαγή σκοτεινής λειτουργίας"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition ${isDarkMode ? 'translate-x-5' : 'translate-x-1'}`}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard 